import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarIcon, 
  ClockIcon, 
  UsersIcon, 
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';
import { 
  getJobStatusColor, 
  getJobStatusLabel,
  mapStatusForCustomer
} from '../utils/statusConstants';

const UPCOMING_PAGE_SIZE = 5;

const isSandboxBuild = process.env.REACT_APP_SANDBOX_MODE === 'true';

const onboardingDismissKey = (customerId) =>
  `customerPortalOnboardingDismissed_${customerId || 'unknown'}`;

const ONBOARDING_STEP_CONFIG = [
  {
    key: 'createdClaimant',
    title: 'Add a claimant',
    description: 'The injured worker this appointment is for.',
    hint: 'Already have claimants on file? You can select an existing one when scheduling.',
    actionLabel: 'Go to Claimants',
    path: '/claimants'
  },
  {
    key: 'createdClaim',
    title: 'Add a claim',
    description: 'The case file with claim number and contact details.',
    hint: 'Already have claims on file? You can select an existing one when scheduling.',
    actionLabel: 'Go to Claims',
    path: '/claims'
  },
  {
    key: 'createdAppointment',
    title: 'Schedule an appointment',
    description: 'Request an Interpreter or Transportation Service',
    actionLabel: 'New Appointment',
    path: '/appointments/new'
  },
  {
    key: 'authorizedAppointment',
    title: 'Authorize when prompted',
    description: 'Approve appointments that need your sign-off.',
    actionLabel: 'View pending authorization',
    path: '/appointments?requiresAuthorization=true',
    authorizationOnly: true
  }
];

const Dashboard = () => {
  const { customer, makeAuthenticatedRequest, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    recentAppointments: [],
    upcomingAppointments: [],
    pendingAuthorizationAppointments: [],
    claimants: [],
    claims: [],
    stats: {
      totalAppointments: 0,
      upcomingCount: 0,
      completedCount: 0,
      pendingAuthorizationCount: 0,
      claimantsCount: 0
    }
  });

  useEffect(() => {
    if (!customer?.id || isSandboxBuild) {
      return;
    }
    setOnboardingDismissed(
      window.localStorage.getItem(onboardingDismissKey(customer.id)) === 'true'
    );
  }, [customer?.id]);

  const isOnboardingStepComplete = (step, onboardingData) => {
    if (step.authorizationOnly && !onboardingData?.authorizationStepApplicable) {
      return true;
    }
    return Boolean(onboardingData?.steps?.[step.key]);
  };

  useEffect(() => {
    // Only load data when authenticated and auth is done loading
    if (!authLoading && isAuthenticated) {
      loadDashboardData();
    }
  }, [isAuthenticated, authLoading]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load all dashboard data in parallel
      const [appointmentsData, claimantsData, claimsData, onboardingData] = await Promise.all([
        makeAuthenticatedRequest(`/appointments?limit=200`),
        makeAuthenticatedRequest(`/claimants`),
        makeAuthenticatedRequest(`/claims`),
        makeAuthenticatedRequest(`/onboarding-status`)
      ]);

      if (onboardingData?.success) {
        setOnboarding(onboardingData.data);
      }

      if (appointmentsData.success && claimantsData.success && claimsData.success) {
        const appointments = appointmentsData.data.appointments || [];
        const claimants = claimantsData.data || [];
        const claims = claimsData.data || [];
        
        console.log('All appointments from API:', appointments);
        console.log('Total appointments count:', appointments.length);

        // Separate upcoming and recent appointments
        const now = new Date();
        const upcomingAppointments = appointments.filter(apt => {
          // Parse the scheduled date and time more robustly
          let scheduledDateTime;
          if (apt.scheduled_date && apt.scheduled_time) {
            // If we have both date and time, combine them
            const dateStr = apt.scheduled_date.split('T')[0]; // Get just the date part
            scheduledDateTime = new Date(`${dateStr}T${apt.scheduled_time}`);
          } else if (apt.scheduled_date) {
            // If we only have date, use the date
            scheduledDateTime = new Date(apt.scheduled_date);
          } else {
            // Skip appointments without proper date
            return false;
          }
          
          // Check if the appointment is in the future (including today)
          const isUpcomingDateTime = scheduledDateTime >= now;
          
          // Include more statuses that should be considered "upcoming"
          const isUpcomingStatus = [
            'finding_interpreter',
            'finding_driver',
            'assigned', 
            'in_progress', 
            'reminders_sent',
            'confirmed',
            'scheduled',
            'authorize_rates'
          ].includes(apt.status);
          
          console.log('Appointment filter check:', {
            id: apt.id,
            scheduled_date: apt.scheduled_date,
            scheduled_time: apt.scheduled_time,
            status: apt.status,
            scheduledDateTime: scheduledDateTime.toISOString(),
            now: now.toISOString(),
            isUpcomingDateTime,
            isUpcomingStatus,
            willShow: isUpcomingDateTime && isUpcomingStatus
          });
          
          return isUpcomingDateTime && isUpcomingStatus;
        });
        
        console.log('Upcoming appointments after filtering:', upcomingAppointments);
        console.log('Upcoming appointments count:', upcomingAppointments.length);
        
        const recentAppointments = appointments.filter(apt => 
          new Date(apt.scheduled_date) < now || 
          ['completed', 'cancelled'].includes(apt.status)
        ).slice(0, 5);

        // Filter appointments that require customer authorization
        // Exclude closed, completed, cancelled, and other final statuses
        // Also include 'completion_report' which displays as 'Completed' to customers
        const excludedStatuses = ['closed', 'completed', 'cancelled', 'billed', 'interpreter_paid', 'no_show', 'rejected', 'completion_report'];
        const pendingAuthorizationAppointments = appointments.filter(apt => {
          console.log('Checking appointment authorization:', { 
            id: apt.id, 
            status: apt.status, 
            customer_authorization_required: apt.customer_authorization_required,
            customer_authorization_obtained: apt.customer_authorization_obtained,
            can_authorize: apt.can_authorize
          });
          return apt.customer_authorization_required && 
                 !apt.customer_authorization_obtained && 
                 apt.can_authorize &&
                 !excludedStatuses.includes(apt.status);
        }).slice(0, 5);

        const completedCount = appointments.filter(apt => apt.status === 'completed').length;
        
        // Use the same excluded statuses for pending authorization count
        const pendingAuthorizationCount = appointments.filter(apt => 
          apt.customer_authorization_required && 
          !apt.customer_authorization_obtained && 
          apt.can_authorize &&
          !excludedStatuses.includes(apt.status)
        ).length;
        
        console.log('Dashboard appointments filtering:');
        console.log('Total appointments:', appointments.length);
        console.log('Appointments with pending_authorization status:', appointments.filter(apt => apt.status === 'pending_authorization').length);
        
        // Group appointments by status for better debugging
        const statusGroups = appointments.reduce((acc, apt) => {
          acc[apt.status] = (acc[apt.status] || 0) + 1;
          return acc;
        }, {});
        console.log('Appointments grouped by status:', statusGroups);
        
        // Show first few appointments with their statuses
        console.log('First 10 appointments:', appointments.slice(0, 10).map(apt => ({ 
          id: apt.id, 
          status: apt.status, 
          job_number: apt.job_number,
          title: apt.title 
        })));

        setDashboardData({
          recentAppointments,
          upcomingAppointments,
          pendingAuthorizationAppointments,
          claimants: claimants.slice(0, 5), // Show first 5 claimants
          claims: claims.slice(0, 5), // Show first 5 claims
          stats: {
            totalAppointments: appointments.length,
            upcomingCount: upcomingAppointments.length,
            completedCount,
            pendingAuthorizationCount,
            claimantsCount: claimants.length
          }
        });
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load dashboard data'));
    } finally {
      setLoading(false);
    }
  };

  const handleDismissOnboarding = () => {
    if (customer?.id && !isSandboxBuild) {
      window.localStorage.setItem(onboardingDismissKey(customer.id), 'true');
    }
    setOnboardingDismissed(true);
  };

  const visibleOnboardingSteps = ONBOARDING_STEP_CONFIG.filter((step) => {
    if (!step.authorizationOnly) return true;
    return onboarding?.authorizationStepApplicable;
  });

  const showOnboardingChecklist =
    onboarding?.showChecklist &&
    !onboardingDismissed &&
    visibleOnboardingSteps.length > 0;

  const completedOnboardingCount = visibleOnboardingSteps.filter(
    (step) => isOnboardingStepComplete(step, onboarding)
  ).length;

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    
    try {
      // Extract date parts to avoid timezone conversion
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      
      // Create date in local timezone (no UTC conversion)
      const localDate = new Date(year, month - 1, day);
      
      if (isNaN(localDate.getTime())) {
        return 'Invalid Date';
      }
      
      return localDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };


  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div>
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-8 text-white">
            <h1 className="text-4xl font-bold text-white mb-2">
              {onboarding?.isFirstTimePortalUser ? `Welcome, ${customer?.name}` : `Welcome back, ${customer?.name}`}
            </h1>
            <p className="text-blue-100 text-lg">
              {showOnboardingChecklist
                ? 'Follow the steps below to schedule your first appointment through the portal.'
                : "Here's an overview of your interpreter services and appointments"}
            </p>
          </div>
        </motion.div>

        {showOnboardingChecklist && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mb-8 bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-sky-50 to-blue-50 px-6 py-4 border-b border-blue-100 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Getting started in the portal</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Progress is based on actions you take here — existing claimants or appointments from other channels do not count.
                </p>
                <p className="text-sm font-medium text-blue-700 mt-2">
                  {completedOnboardingCount} of {visibleOnboardingSteps.length} steps completed
                </p>
              </div>
              <button
                type="button"
                onClick={handleDismissOnboarding}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                title="Dismiss checklist"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {visibleOnboardingSteps.map((step, index) => {
                const completed = isOnboardingStepComplete(step, onboarding);
                return (
                  <div
                    key={step.key}
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border p-4 ${
                      completed ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                        completed ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {completed ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <span className="text-sm font-semibold">{index + 1}</span>
                        )}
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">{step.title}</h3>
                        <p className="text-sm text-gray-600">{step.description}</p>
                        {step.hint && !completed && (
                          <p className="text-xs text-gray-500 mt-1">{step.hint}</p>
                        )}
                      </div>
                    </div>
                    {!completed && (
                      <button
                        type="button"
                        onClick={() => navigate(step.path)}
                        className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 whitespace-nowrap"
                      >
                        {step.actionLabel}
                        <ArrowRightIcon className="ml-1 h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-xl flex-shrink-0">
                <CalendarIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide truncate">Total Appointments</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.totalAppointments}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-xl flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide truncate">Upcoming</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.upcomingCount}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center">
              <div className="bg-purple-100 p-3 rounded-xl flex-shrink-0">
                <UsersIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide truncate">Claimants</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.claimantsCount}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center">
              <div className="bg-amber-100 p-3 rounded-xl flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-amber-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide truncate">Pending Authorization</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.pendingAuthorizationCount}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center">
              <div className="bg-orange-100 p-3 rounded-xl flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide truncate">Completed</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.completedCount}</p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upcoming Appointments */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <div className="bg-green-600 p-2 rounded-lg mr-3">
                    <CalendarIcon className="h-6 w-6 text-white" />
                  </div>
                  Upcoming Appointments
                </h2>
                <button
                  onClick={() => navigate('/appointments/new')}
                  className="flex items-center text-blue-600 hover:text-blue-500 font-medium"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  New
                </button>
              </div>
            </div>
            <div className="p-6">
              {dashboardData.upcomingAppointments.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No upcoming appointments</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Schedule a new appointment to get started.
                  </p>
                  <button
                    onClick={() => navigate('/appointments/new')}
                    className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Schedule Appointment
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData.upcomingAppointments
                    .slice((upcomingPage - 1) * UPCOMING_PAGE_SIZE, upcomingPage * UPCOMING_PAGE_SIZE)
                    .map((appointment) => (
                    <div 
                      key={appointment.id} 
                      className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-colors"
                      onClick={() => navigate(`/appointments/${appointment.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{appointment.job_number || appointment.title}</h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {formatDate(appointment.scheduled_date)} at {formatTime(appointment.scheduled_time)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {appointment.claimant_first_name} {appointment.claimant_last_name}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getJobStatusColor(appointment)}`}>
                            {getJobStatusLabel(appointment)}
                          </span>
                          <EyeIcon className="h-4 w-4 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Pagination for upcoming appointments */}
                  {dashboardData.upcomingAppointments.length > UPCOMING_PAGE_SIZE && (
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <span className="text-sm text-gray-500">
                        Showing {((upcomingPage - 1) * UPCOMING_PAGE_SIZE) + 1}-{Math.min(upcomingPage * UPCOMING_PAGE_SIZE, dashboardData.upcomingAppointments.length)} of {dashboardData.upcomingAppointments.length}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setUpcomingPage(p => Math.max(1, p - 1))}
                          disabled={upcomingPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          {upcomingPage} / {Math.ceil(dashboardData.upcomingAppointments.length / UPCOMING_PAGE_SIZE)}
                        </span>
                        <button
                          onClick={() => setUpcomingPage(p => Math.min(Math.ceil(dashboardData.upcomingAppointments.length / UPCOMING_PAGE_SIZE), p + 1))}
                          disabled={upcomingPage >= Math.ceil(dashboardData.upcomingAppointments.length / UPCOMING_PAGE_SIZE)}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => navigate('/appointments')}
                    className="w-full text-center text-blue-600 hover:text-blue-500 font-medium py-2"
                  >
                    View All Appointments
                    <ArrowRightIcon className="h-4 w-4 ml-1 inline" />
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-purple-100">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="bg-purple-600 p-2 rounded-lg mr-3">
                  <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                Recent Appointments
              </h2>
            </div>
            <div className="p-6">
              {dashboardData.recentAppointments.length === 0 ? (
                <div className="text-center py-8">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No recent appointments</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Your appointment history will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData.recentAppointments.map((appointment) => (
                    <div 
                      key={appointment.id} 
                      className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-colors"
                      onClick={() => navigate(`/appointments/${appointment.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{appointment.job_number || appointment.title}</h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {formatDate(appointment.scheduled_date)} at {formatTime(appointment.scheduled_time)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {appointment.claimant_first_name} {appointment.claimant_last_name}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getJobStatusColor(appointment)}`}>
                            {getJobStatusLabel(appointment)}
                          </span>
                          <EyeIcon className="h-4 w-4 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => navigate('/appointments')}
                    className="w-full text-center text-blue-600 hover:text-blue-500 font-medium py-2"
                  >
                    View All Appointments
                    <ArrowRightIcon className="h-4 w-4 ml-1 inline" />
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Pending Authorization */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 px-6 py-4 border-b border-amber-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <div className="bg-amber-600 p-2 rounded-lg mr-3">
                    <ClockIcon className="h-6 w-6 text-white" />
                  </div>
                  Pending Authorization
                </h2>
                <button
                  onClick={() => navigate('/appointments?requiresAuthorization=true')}
                  className="flex items-center text-amber-600 hover:text-amber-500 font-medium"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  View All
                </button>
              </div>
            </div>
            <div className="p-6">
              {dashboardData.pendingAuthorizationAppointments.length === 0 ? (
                <div className="text-center py-8">
                  <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No pending authorizations</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    All appointments are up to date.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData.pendingAuthorizationAppointments.map((appointment) => (
                    <div 
                      key={appointment.id} 
                      className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-colors"
                      onClick={() => navigate(`/appointments/${appointment.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{appointment.job_number || appointment.title}</h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {formatDate(appointment.scheduled_date)} at {formatTime(appointment.scheduled_time)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {appointment.claimant_first_name} {appointment.claimant_last_name}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getJobStatusColor(appointment)}`}>
                            {getJobStatusLabel(appointment)}
                          </span>
                          <EyeIcon className="h-4 w-4 text-amber-600" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => navigate('/appointments?requiresAuthorization=true')}
                    className="w-full text-center text-amber-600 hover:text-amber-500 font-medium py-2"
                  >
                    View All Requiring Authorization
                    <ArrowRightIcon className="h-4 w-4 ml-1 inline" />
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 bg-white rounded-lg shadow p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/appointments/new')}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <PlusIcon className="h-8 w-8 text-blue-600 mr-3" />
              <div className="text-left">
                <h3 className="font-medium text-gray-900">Schedule New Appointment</h3>
                <p className="text-sm text-gray-500">Request an interpreter for a new appointment</p>
              </div>
            </button>
            
            <button
              onClick={() => navigate('/appointments')}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <CalendarIcon className="h-8 w-8 text-green-600 mr-3" />
              <div className="text-left">
                <h3 className="font-medium text-gray-900">View All Appointments</h3>
                <p className="text-sm text-gray-500">See your complete appointment history</p>
              </div>
            </button>
            
            <div className="flex items-center p-4 border border-gray-200 rounded-lg">
              <UsersIcon className="h-8 w-8 text-purple-600 mr-3" />
              <div className="text-left">
                <h3 className="font-medium text-gray-900">Manage Claimants</h3>
                <p className="text-sm text-gray-500">View and update claimant information</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
